// Raynet API Error types
export class RaynetError extends Error {
  constructor(
    message: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public readonly status: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public readonly code?: string
  ) {
    super(message);
    this.name = 'RaynetError';
  }
}

// Form submission data
export interface FormSubmissionData {
  // Osobní údaje
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  
  // Adresa
  street?: string;
  city?: string;
  postalCode?: string;
  
  // Zájem o kamna
  stoveType?: string;
  heatingArea?: string;
  budget?: string;
  installation?: string;
  
  // Poznámky
  message?: string;
  
  // Metadata
  source?: string;
  gdprConsent: boolean;
  marketingConsent?: boolean;
}

// Dotazník pro prodejce kamen
export interface DealerSurveyData {
  // Identifikace prodejce
  companyName: string;
  ico?: string; // IČO firmy (z Raynetu nebo ARES)
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  
  // Strukturovaná adresa showroomu/provozovny
  showroomStreet: string;
  showroomCity: string;
  showroomZipCode: string;
  showroomCountry: string;
  hasNoShowroom?: boolean; // Příznak, že firma nemá showroom
  
  visitDate: string;
  salesRep: string;
  salesRepPersonId?: number;
  
  // Raynet data (if selected from search)
  selectedRaynetCompany?: RaynetCompany;
  selectedRaynetContactPerson?: RaynetContactPerson;
  selectedRaynetAddress?: RaynetAddress;
  
  // ARES data (if selected from ARES search)
  selectedAresCompany?: AresCompany;
  
  // Zájem a reakce
  designImpression?: string;
  cooperationInterest?: string;
  brandReaction?: string;
  interests?: string[];
  nextSteps?: string[];
  otherStep?: string;
  comments?: string;
  
  // Produkt a cena - vyplňuje společně
  whatAttracted?: string;
  whatMissing?: string;
  competitors?: string[]; // Changed to array of tags
  pricePerception?: string;
  regionPotential?: string;
  showroomInterest?: string;
  showroomColorPreference?: string;
  marketingMaterials?: string;
  
  // Obchodní podmínky
  expectedSupport?: string[];
  otherSupport?: string;
  salesMethod?: string[]; // Changed to array for multi-choice
  maxDeliveryTime?: string;
  
  // Prodejní potenciál
  typicalCustomer?: string;
  totalStovesPerYear?: string;
  designStovesPerYear?: string;
  
  // Metadata
  source?: string;
}

// Competitor tag interface
export interface CompetitorTag {
  id: number;
  name: string;
  usage_count: number;
  created_at?: string;
  updated_at?: string;
}

// Raynet API Response types
export interface RaynetResponse<T = any> {
  success: boolean;
  data?: T;
  totalCount?: number;
  message?: string;
}

// Raynet Lead data
export interface RaynetLead {
  id?: number;
  name: string;
  firstName?: string;
  lastName?: string;
  companyId?: number;
  personId?: number;
  owner?: number;
  category?: number;
  priority?: string;
  rating?: string;
  state?: string;
  source?: number;
  topic?: string;
  description?: string;
  customFields?: Record<string, any>;
}

// Raynet Person data
export interface RaynetPerson {
  id?: number;
  firstName: string;
  lastName: string;
  titleBefore?: string;
  titleAfter?: string;
  birthday?: string;
  companyId?: number;
  owner?: number;
  category?: number;
  role?: string;
  primaryEmail?: string;
  emails?: Array<{
    email: string;
    primary: boolean;
  }>;
  telephones?: Array<{
    telephone: string;
    primary: boolean;
  }>;
  addresses?: Array<{
    address: {
      name?: string;
      street?: string;
      city?: string;
      zipCode?: string;
      country?: string;
    };
    primary: boolean;
  }>;
  customFields?: Record<string, any>;
}

// Raynet dropdown options
export interface RaynetDropdownOption {
  id: number;
  code01?: string;
  name: string;
  note?: string;
}

// Raynet Address data
export interface RaynetAddress {
  id: number;
  name: string;
  street: string;
  city: string;
  zipCode: string;
  country: string;
  province?: string;
  isPrimary: boolean;
  isContactAddress: boolean;
  contactInfo?: any;
}

// Raynet Company data for search results
export interface RaynetCompany {
  id: number;
  name: string;
  ico?: string;
  addresses?: RaynetAddress[];
  phone?: string;
  email?: string;
  contactPersons?: Array<{
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
  }>;
}

// Raynet Contact Person data
export interface RaynetContactPerson {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email?: string;
  phone?: string;
  position?: string;
  companyId: number;
}

// Configuration types
export interface AppConfig {
  raynetApiKey?: string;
  raynetInstance?: string;
  raynetUser?: string;
  formTitle?: string;
  formDescription?: string;
  defaultLeadCategory?: number;
  defaultLeadSource?: number;
  offlineMode?: boolean;
}

// Offline queue types
export interface OfflineRequest {
  id: string;
  type: 'submission' | 'lead' | 'person';
  payload: any;
  createdAt: string;
  status: 'pending' | 'processing' | 'sent' | 'failed';
  retryCount: number;
  errorMessage?: string;
  lastAttempt?: string;
}

// Submission history
export interface SubmissionHistoryItem {
  id: string;
  submissionData: FormSubmissionData;
  raynetResponse?: any;
  status: 'pending' | 'sent' | 'failed';
  createdAt: string;
  processedAt?: string;
}

// ARES Company data
export interface AresCompany {
  ico: string;
  name: string;
  legalForm?: string;
  address?: {
    street: string;
    city: string;
    zipCode: string;
    country: string;
    district?: string;
    region?: string;
  } | null;
  established?: string;
  active: boolean;
  source: 'ARES';
  dic?: string;
  activities?: any[];
  size?: string;
}

// Combined company type (Raynet + ARES)
export interface CompanySearchResult {
  // Common fields
  name: string;
  ico?: string;
  address?: any;
  source: 'RAYNET' | 'ARES';
  
  // Raynet specific
  id?: number;
  addresses?: RaynetAddress[];
  phone?: string;
  email?: string;
  contactPersons?: Array<{
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
  }>;
  
  // ARES specific
  legalForm?: string;
  established?: string;
  active?: boolean;
  dic?: string;
  activities?: any[];
  size?: string;
}

// API status
export interface ApiStatus {
  isConnected: boolean;
  lastCheck: string;
  latency?: number;
  error?: string;
}

// Raynet User Account data
export interface RaynetUser {
  id: number;
  username: string;
  person: {
    id: number;
    fullName: string;
  };
  userRole: string;
} 

// Raynet Company Creation Request
export interface RaynetCompanyCreateRequest {
  name: string;
  regNumber?: string; // IČO
  legalForm?: string;
  rating?: number;
  state?: string;
  role?: string;
  category?: number;
  // Kontaktní informace
  contactInfo?: {
    email?: string;
    tel1?: string;
    tel2?: string;
    www?: string;
  };
  // Adresa (primární)
  address?: {
    name?: string;
    street?: string;
    city?: string;
    zipCode?: string;
    country?: string;
    province?: string;
  };
  // Vlastníci a další údaje
  owner?: number;
  customFields?: Record<string, any>;
}

// Raynet Company Creation Response
export interface RaynetCompanyCreateResponse {
  success: boolean;
  data?: {
    id: number;
  };
  message?: string;
}

// Raynet Address Creation Request
export interface RaynetAddressCreateRequest {
  address: {
    name?: string;
    street: string;
    city: string;
    zipCode: string;
    country: string;
    province?: string;
  };
  primary?: boolean;
  contactAddress?: boolean;
  contactInfo?: {
    email?: string;
    tel1?: string;
    tel2?: string;
  };
}

// Raynet Address Creation Response
export interface RaynetAddressCreateResponse {
  success: boolean;
  data?: {
    id: number;
  };
  message?: string;
}

// Raynet Contact Person Creation Request
export interface RaynetContactPersonCreateRequest {
  firstName: string;
  lastName: string;
  titleBefore?: string;
  titleAfter?: string;
  companyId: number;
  // Kontaktní informace
  contactInfo?: {
    email?: string;
    tel1?: string;
    tel2?: string;
  };
  // Relationship s firmou
  primaryRelationship?: {
    company: number;
    type?: string;
  };
  // Vlastníci a další údaje
  owner?: number;
  category?: number;
  customFields?: Record<string, any>;
}

// Raynet Contact Person Creation Response
export interface RaynetContactPersonCreateResponse {
  success: boolean;
  data?: {
    id: number;
  };
  message?: string;
}

// Raynet Meeting Creation Request
export interface RaynetMeetingCreateRequest {
  title: string;
  description?: string;
  solution?: string; // JSON data uložená jako string
  scheduledFrom: string; // ISO datum
  scheduledTill?: string; // ISO datum
  // Participanti
  primaryParticipant?: {
    company?: number;
    person?: number;
  };
  participants?: Array<{
    company?: number;
    person?: number;
    role?: string;
  }>;
  // Typ aktivity
  activityType?: string;
  // Vlastníci a další údaje
  owner?: number;
  category?: number;
  customFields?: Record<string, any>;
}

// Raynet Meeting Creation Response
export interface RaynetMeetingCreateResponse {
  success: boolean;
  data?: {
    id: number;
  };
  message?: string;
}

// Survey Submission Request - komplexní request pro odeslání dotazníku
export interface SurveySubmissionRequest {
  surveyData: DealerSurveyData;
  // Metadata
  representative: string;
  submissionDate: string;
}

// Survey Submission Response
export interface SurveySubmissionResponse {
  success: boolean;
  data?: {
    companyId: number;
    contactPersonId: number;
    meetingId: number;
    // Označení co bylo vytvořeno vs propojeno
    createdEntities: {
      company: boolean;
      contactPerson: boolean;
      address: boolean;
    };
  };
  message?: string;
} 